/***
 * Summary: here resides the scheme and roles for the fields of the network performing
 * 
 */
import { yupToFormErrors } from "formik";
import * as Yup from "yup";

const createNumberValidation = (fieldName) =>
  Yup.number()
    .typeError(`Invalid data. '${fieldName}' must be a number`)
    .nullable(true)
    .test('is-number', `Invalid data. '${fieldName}' must be a number`, function (value) {
      return (value === null || value === undefined || !isNaN(value)) || this.createError({ message: `Invalid data. '${fieldName}' must be a number` });
    });

const TODIS_LSAEPORTS_SCHEMA = Yup.object().shape({
  //general section Rules:
  nameResp: Yup.string("Road ID is alphanumeric").nullable(true),
  addressResp: Yup.string("Road ID is alphanumeric").nullable(true),
  contactPer: Yup.string("Road ID is alphanumeric").nullable(true),
  contactPos: Yup.string("Road ID is alphanumeric").nullable(true),
  contactNum: Yup.string()
    .transform((originalValue) => {
      // Si el valor comienza con '+', eliminamos ese símbolo
      return typeof originalValue == 'string' ? originalValue.replace(/^\+/, '') : originalValue;
    })
    .nullable(true)
    .test('is-valid', 'Invalid data. \'Phone Number\' must be a valid number', function (value) {
      // Permitir números, espacios, y la presencia de '+'
      const isValid = /^[0-9+\s]+$/.test(value) || value == null;

      if (!isValid) {
        throw this.createError({ message: 'Invalid data. \'Phone Number\' must be a valid number' });
      }

      return true;
    }),

  contactEmail: Yup.string("Email")
    .transform((originalValue) => {
      // Realizar transformaciones si es necesario
      return typeof originalValue === 'string' ? originalValue.trim() : originalValue;
    })
    .nullable(true)
    .test('is-email', 'Invalid data. \'Email\' must be valid', function (value) {
      // Si el valor es null o undefined, lo consideramos válido y no aplicamos más validaciones
      if (value === null || value === undefined) {
        return true;
      }

      const emailRegex = /^[a-zA-Z0-9._-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,6}$/;

      // Verificar si es una cadena antes de intentar dividirla
      if (typeof value === 'string') {
        const emails = value.split(/[,;:]+/);  // Separar por comas, punto y coma, o dos puntos

        for (const email of emails) {
          if (!emailRegex.test(email.trim())) {
            throw this.createError({ message: `Invalid data. '${email}' must be a valid email address` });
          }
        }
      }

      return true;
    }),
  countryCode: Yup.string("Country Code").nullable(true),
  tentTCategory: Yup.string("tent category is alphanumeric").nullable(true),
  nodeNameval: Yup.string("End Node Name is alphanumeric"),
  /*   dateValidTo: Yup.date("Valid from is a Date").required().nullable(true),
    dateValidFrom: Yup.date("Valid to is a Date").required().nullable(true), */
  dateValidFor: Yup.string("Valid for is a Year").nullable(true),
  remarks: Yup.string("remarks").nullable(true),

  seaportsLivePmDescDTO: Yup.object().shape({
    projectName: Yup.string('Value should be a text').nullable(true),
    typeForeseenInterven: Yup.string('Value should be a text').nullable(true),
    length: Yup.string('Value should be a text').nullable(true),
    totalCostCapex: createNumberValidation('Total Cost (CAPEX) (Euro)'),
    estiImpDeadline: Yup.string('Value should be a text').nullable(true),
    remarks: Yup.string("remarks").nullable(true),
    linearproject: Yup.boolean().nullable(true),
  }),
  seaportsLivePmEligiTentDTO: Yup.object().shape({
    railConn: Yup.string('Value should be a text').nullable(true),
    roadConn: Yup.string('Value should be a text').nullable(true),
    cleanFuelsAvail: Yup.string('Value should be a text').nullable(true),
    terminalAvail: Yup.string('Value should be a text').nullable(true),
    vtmisDeploy: Yup.string('Value should be a text').nullable(true),
    iwwCemtConn: Yup.string('Value should be a text').nullable(true),
    wasteFacilities: Yup.string('Value should be a text').nullable(true),
    remarks: Yup.string("remarks").nullable(true),
    intermodalUnitsHandling: Yup.string('Value should be a text').nullable(true),
    seaCanalsConnection: Yup.boolean().nullable(true),
    multimodalFreightTerminal: Yup.boolean().nullable(true),
    alternativeFuelsElectricity: Yup.boolean().nullable(true),
    alternativeFuelsMethane: Yup.boolean().nullable(true),
  }),
  seaportsLivePmIndicatorsDTO: Yup.object().shape({
    siAnnTraffDemGro: createNumberValidation('Annual traffic demand growth (%)'),
    siModalTrans: createNumberValidation('Modal transfer - if applicable (%)'),
    siAnnTraffAcciRateRed: createNumberValidation('Annual accident rate reduction - if applicable (%)'),
    eiEirr: createNumberValidation('Economic internal rate of return (%)'),
    eiNpv: createNumberValidation('Net present value (Euro)'),
    eiSdr: createNumberValidation('Social discount rate (%)'),
    eiProjPlanDesCost: createNumberValidation('Project planning & design cost (Euro)'),
    eiProjConstCost: createNumberValidation('Project construction cost (Euro)'),
    eiTotalProjCost: createNumberValidation('Total project cost (Euro)'),
    fiFirr: createNumberValidation('Financial internal rate of return (%)'),
    fiFnpv: createNumberValidation('Financial net present value (Euro)'),
    fiFdr: createNumberValidation('Financial discount rate (%)'),
    fiWacc: createNumberValidation('Weighted average cost of capital (%)'),
    fiFirtsYearProf: Yup.date("Valid from is a Date").nullable(true),
    fiDscr: createNumberValidation('Debt service coverage ratio (%)'),
    eniCo2Ems: createNumberValidation('CO2 emissions (+/- %)'),
    eniNoxEms: createNumberValidation('Nox emissions (+/- %)'),
    eniSo2Ems: createNumberValidation('SO2 emissions (+/- %)'),
    eniNmhcEms: createNumberValidation('Non-methane hydrocarbons (+/- %)'),
    eniPpm: createNumberValidation('Particulate matter – ppm (+/- %)'),
    eniNoiseLevel: createNumberValidation('TODIS'),
    eniCliChangeRes: createNumberValidation('Climate change resilience (text)'),
    eniProteNatuaAff: createNumberValidation('Protected natural areas affected (km2)'),
    remarks: Yup.string("remarks").nullable(true),
  }),
  seaportsLivePmPiDTO: Yup.object().shape({
    forecastConstPeriod: Yup.number('Value should be number').nullable(true),
    actualConstPeriod: Yup.number('Value should be number').nullable(true),
    forecastCapex: Yup.number('Value should be number').nullable(true),
    actualCapex: Yup.number('Value should be number').nullable(true),
    forecastOpex: Yup.number('Value should be number').nullable(true),
    actualOpex: Yup.number('Value should be number').nullable(true),
    forecastMaintCost: Yup.number('Value should be number').nullable(true),
    actualMaintCost: Yup.number('Value should be number').nullable(true),
    interestDuringConst: Yup.number('Value should be number').nullable(true),
    ebitda: Yup.number('Value should be number').nullable(true),
    forecastRevenue: Yup.number('Value should be number').nullable(true),
    actualRevenue: Yup.number('Value should be number').nullable(true),
    forecastPortTraffic: Yup.number('Value should be number').nullable(true),
    actualPortTraffic: Yup.number('Value should be number').nullable(true),
    forecastPassTraffic: Yup.number('Value should be number').nullable(true),
    actualPassTraffic: Yup.number('Value should be number').nullable(true),
    forecastFreightTn: Yup.number('Value should be number').nullable(true),
    actualFreightTn: Yup.number('Value should be number').nullable(true),
    remarks: Yup.string("remarks").nullable(true)
  }),
  seaportsLivePmStatusDTO: Yup.object().shape({
    implemented: Yup.string('Value should be a text').nullable(true),
    ongoingProjFs: Yup.string('Value should be a text').nullable(true),
    matureProjFs: Yup.string('Value should be a text').nullable(true),
    projUnderPrep: Yup.string('Value should be a text').nullable(true),
    projStatusDesc: Yup.string('Value should be a text').nullable(true),
    initProjCompDate: Yup.date("Valid to is a Date").nullable(true),
    actualProjCompDate: Yup.date("Valid to is a Date").nullable(true),
    projFundNatBudg: createNumberValidation('National Budget'),
    projFundWb: createNumberValidation('WB'),
    projFundEbrd: createNumberValidation('EBRD'),
    projFundEib: createNumberValidation('EIB'),
    otherIfi: createNumberValidation('IFI'),
    otherIfiSpec: Yup.string('Value should be a text').nullable(true),
    concessions: createNumberValidation('Concessions'),
    concessionSpec: Yup.string('Value should be a text').nullable(true),
    euFund: createNumberValidation('EU Fund'),
    euFundSpec: Yup.string('Value should be a text').nullable(true),
    otherFund: createNumberValidation('Other funding source Specify'),
    otherFundSpec: Yup.string('Value should be a text').nullable(true),
    projDocTitle: Yup.string('Value should be a text').nullable(true),
    projDocPrepBy: Yup.string('Value should be a text').nullable(true),
    projDocSupervBy: Yup.string('Value should be a text').nullable(true),
    remarks: Yup.string("remarks").nullable(true),
  }),
  seaportsLivePmTentcompDTO: Yup.object().shape({
    befRailConn: Yup.string('Value should be a text').nullable(true),
    aftRailConn: Yup.string('Value should be a text').nullable(true),
    befCmtConn: Yup.string('Value should be a text').nullable(true),
    aftCmtConn: Yup.string('Value should be a text').nullable(true),
    befCleanFuels: Yup.string('Value should be a text').nullable(true),
    aftCleanFuels: Yup.string('Value should be a text').nullable(true),
    befTerminalAvail: Yup.string('Value should be a text').nullable(true),
    aftTerminalAvail: Yup.string('Value should be a text').nullable(true),
    befRoadConn: Yup.string('Value should be a text').nullable(true),
    aftRoadConn: Yup.string('Value should be a text').nullable(true),
    befWasteFacilities: Yup.string('Value should be a text').nullable(true),
    aftWasteFacilities: Yup.string('Value should be a text').nullable(true),
    remarks: Yup.string("remarks").nullable(true),
    befIntermodalUnitsHandling: Yup.string('Value should be a text').nullable(true),
    aftIntermodalUnitsHandling: Yup.string('Value should be a text').nullable(true),
    befMultimodalFreightTerminal: Yup.boolean().nullable(true),
    aftMultimodalFreightTerminal: Yup.boolean().nullable(true),
    befSeaCanalsConnection: Yup.boolean().nullable(true),
    aftSeaCanalsConnection: Yup.boolean().nullable(true),
    befConnectionTwoModesTransport: Yup.boolean().nullable(true),
    aftConnectionTwoModesTransport: Yup.boolean().nullable(true),
  })
});

const LSAEPORTS_INIT = {
  general_road_id: "",
 /*  dateValidTo: "",
  dateValidFrom: "", */
  dateValidFor: "",
  tentTCategory: "",
  riverId: "",
  internationalComm: "",
  startNodeName: "",
  nodeNameval: "",
  nameResp: "",
  addressResp: "",
  contactPer: "",
  contactPos: "",
  contactNum: 0,
  contactEmail: "",
  countryCode: "",
  remarks: "",
  seaportsLivePmDescDTO: {
    projectName: "",
    typeForeseenInterven: "",
    length: "",
    totalCostCapex: "",
    estiImpDeadline: "",
    linearproject: false,
    remarks: ""
  },
  seaportsLivePmEligiTentDTO: {
    railConn: false,
    roadConn: false,
    cleanFuelsAvai: false,
    terminalAvai: false,
    vtmisDeploy: false,
    iwwCemtConn: false,
    wasteFacilities: false,
    remarks: "",
    intermodalUnitsHandling: false,
    seaCanalsConnection: false,
    multimodalFreightTerminal: false,
    alternativeFuelsElectricity: false,
    alternativeFuelsMethane: false,
  },
  seaportsLivePmIndicatorsDTO: {
    siAnnTraffDemGro: "",
    siModalTrans: "",
    siAnnTraffAcciRateRed: "",
    eiEirr: "",
    eiNpv: "",
    eiSdr: "",
    eiProjPlanDesCost: "",
    eiProjConstCost: "",
    eiTotalProjCost: "",
    fiFirr: "",
    fiFnpv: "",
    fiFdr: "",
    fiWacc: "",
    fiFirtsYearProf: "",
    fiDscr: "",
    eniCo2Ems: "",
    eniNoxEms: "",
    eniSo2Ems: "",
    eniNmhcEms: "",
    eniPpm: "",
    eniNoiseLevel: "",
    eniCliChangeRes: "",
    eniProteNatuaAff: "",
    remarks: ""
  },
  seaportsLivePmPiDTO: {
    forecastConstPeriod: "",
    actualConstPeriod: "",
    forecastCapex: "",
    actualCapex: "",
    forecastOpex: "",
    actualOpex: "",
    forecastMaintCost: "",
    actualMaintCost: "",
    interestDuringConst: "",
    ebitda: "",
    forecastRevenue: "",
    actualRevenue: "",
    forecastPortTraffic: "",
    actualPortTraffic: "",
    forecastPassTraffic: "",
    actualPassTraffic: "",
    forecastFreightTn: "",
    actualFreightTn: "",
    remarks: ""
  },
  seaportsLivePmStatusDTO: {
    implemented: "",
    ongoingProjFs: "",
    matureProjFs: "",
    projUnderPrep: "",
    projStatusDesc: "",
    initProjCompDate: "",
    actualProjCompDate: "",
    projFundNatBudg: "",
    projFundWb: "",
    projFundEbrd: "",
    projFundEib: "",
    otherIfi: "",
    otherIfiSpec: "",
    concessions: "",
    concessionSpec: "",
    euFund: "",
    euFundSpec: "",
    otherFund: "",
    otherFundSpec: "",
    projDocTitle: "",
    projDocPrepBy: "",
    projDocSupervBy: "",
    remarks: ""
  },
  seaportsLivePmTentcompDTO: {
    befRailConn: false,
    aftRailConn: false,
    befCmtConn: false,
    aftCmtConn: false,
    befCleanFuels: false,
    aftCleanFuels: false,
    befTerminalAvail: false,
    aftTerminalAvail: false,
    befRoadConn: false,
    aftRoadConn: false,
    befWasteFacilities: false,
    aftWasteFacilities: false,
    befConnectionTwoModesTransport: false,
    aftConnectionTwoModesTransport: false,
    remarks: ""
  }
}

export { TODIS_LSAEPORTS_SCHEMA, LSAEPORTS_INIT };